import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { IconButton, IconButtonProps, InputAdornment as MuiInputAdornment, Typography } from '@material-ui/core'

import { Add, Close } from '@material-ui/icons'
import { Button, ButtonProps } from 'components/inputs/Button/Button'
import { TextField } from 'components/inputs/TextField/TextField'
import { GroupSelect } from 'components/data/GroupSelect/GroupSelect'
import { KnowledgeBaseVisibilitySelect } from 'components/data/VisibilityTypeSelect/KnowledgeBaseVisibilitySelect/KnowledgeBaseVisibilitySelect'
import { UploadImageField } from 'components/inputs/UploadImageField/UploadImageField'
import { MultipleValuesField } from 'components/inputs/MultipleValuesField/MultipleValuesField'
import { ContentSection } from 'components/data/ContentSection/ContentSection'
import { LanguagesSelect } from 'components/data/LanguagesSelect/LanguagesSelect'
import { LabelSelect } from 'components/data/LabelSelect/LabelSelect'
import { Checkbox } from 'components/inputs/Checkbox/Checkbox'

const selectStyles = css`
  min-width: 200px;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  max-width: 800px;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const InfoSection = styled(ContentSection)`
  flex: 1;
  margin-top: ${({ theme }) => theme.spacing(2.5)}px;
  min-width: 200px;
`

const CheckboxInfoSection = styled(ContentSection)`
  flex: 1;
  margin-top: ${({ theme }) => theme.spacing(2.5)}px;
  max-width: fit-content;
  white-space: pre;
`

const StyledGroupSelect = styled(GroupSelect)`
  ${selectStyles}
`

const StyledKnowledgeBaseVisibilitySelect = styled(KnowledgeBaseVisibilitySelect)`
  ${selectStyles}
`

const DestinationValue = styled(Typography)`
  margin-top: ${({ theme }) => theme.spacing(2)}px;
`

const StyledTextField = styled(TextField)`
  width: 100%;
`

const StyledTextFieldShort = styled(TextField)`
  max-width: 200px;
`

const StyledUploadImageField = styled(UploadImageField)`
  width: 200px;
`

const StyledMultipleValuesField = styled(MultipleValuesField)`
  max-height: 240px;
`

const StyledCheckbox = styled(Checkbox)`
  width: fit-content;
  height: fit-content;
  margin-top: ${({ theme }) => theme.spacing(1)}px;
`

const RowContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  grid-column-gap: 20px;
`

const MetadataRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing(2)}px;
`

const AddMetadataButton = styled((props: IconButtonProps) => (
  <IconButton {...props}>
    <Add />
  </IconButton>
))`
  width: fit-content;
  height: fit-content;
`

const RemoveMetadataButton = styled((props: IconButtonProps) => (
  <IconButton {...props}>
    <Close />
  </IconButton>
))`
  width: fit-content;
  height: fit-content;
  margin-top: ${({ theme }) => theme.spacing(1)}px;
`

interface SaveButtonProps extends ButtonProps {
  $isMobile?: boolean
}

const SaveButton = styled((props: SaveButtonProps) => (
  <Button {...props} />
), { shouldForwardProp: (prop) => prop !== '$isMobile' })`
  ${({ theme, $isMobile }) => css`
    width: auto;
    z-index: 999;
    ${$isMobile ? css`
      margin-left: ${theme.spacing(2.5)}px;
    ` : css`
      position: absolute;
      bottom: 50px;
      right: 50px;
    `}
  `}
`

const DifficultySliderContainer = styled.div`
  padding: ${({ theme }) => theme.spacing(1)}px ${({ theme }) => theme.spacing(5)}px;
`

const InputAdornment = styled(MuiInputAdornment)`
  margin-right: ${({ theme }) => theme.spacing(2)}px;
`

const QuestionsListButton = styled(Button)`
  margin: ${({ theme }) => theme.spacing(1)}px;
  max-width: 250px;
`

const TranslationLanguagesSelect = styled(LanguagesSelect)`
  ${selectStyles}
`

const MetadataHeader = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)}px;
`

const StyledLabelSelect = styled(LabelSelect)`
  ${selectStyles}
`

export {
  Form,
  InfoSection,
  SaveButton,
  StyledGroupSelect as GroupSelect,
  StyledTextField as TextField,
  RowContainer,
  StyledKnowledgeBaseVisibilitySelect as KnowledgeBaseVisibilitySelect,
  DestinationValue,
  HeaderContainer,
  StyledUploadImageField as UploadImageField,
  StyledMultipleValuesField as MultipleValuesField,
  DifficultySliderContainer,
  StyledTextFieldShort as TextFieldShort,
  InputAdornment,
  QuestionsListButton,
  TranslationLanguagesSelect,
  MetadataRow,
  AddMetadataButton,
  RemoveMetadataButton,
  MetadataHeader,
  StyledCheckbox as Checkbox,
  CheckboxInfoSection,
  StyledLabelSelect as LabelSelect,
}
